import { useDebounce } from '@uidotdev/usehooks'
import { useEffect, useEffectEvent, useRef, useState, type FocusEvent, type ReactNode } from 'react'

import { Input, type InputProps } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

import { ComboboxListbox, useComboboxListbox } from './combobox'

type AsyncAutocompleteInputOwnProps<TOption> = {
  id?: string
  label?: ReactNode
  placeholder?: string
  className?: string
  inputClassName?: string
  value?: string | null
  fetchOptions: (input: string) => Promise<readonly TOption[] | null | undefined>
  minChars?: number
  renderOption?: (option: TOption) => ReactNode
  onChange?: (value: string) => void
  onOptionSelected?: (option: TOption) => void
  onFetchError?: (error: unknown) => void
  allowAutofill?: boolean
  disabled?: boolean
  dropdownInPortal?: boolean
  containWheel?: boolean
}

export type AsyncAutocompleteInputProps<TOption> = AsyncAutocompleteInputOwnProps<TOption> &
  Omit<InputProps, keyof AsyncAutocompleteInputOwnProps<TOption>>

export default function AsyncAutocompleteInput<TOption>({
  id,
  label,
  placeholder,
  className,
  inputClassName,
  value,
  fetchOptions,
  minChars = 3,
  renderOption,
  onChange,
  onOptionSelected,
  onFetchError,
  allowAutofill = false,
  disabled = false,
  dropdownInPortal = false,
  containWheel = false,
  ...props
}: AsyncAutocompleteInputProps<TOption>) {
  const [options, setOptions] = useState<readonly TOption[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const fetchSequenceRef = useRef(0)
  const inputValue = value ?? ''
  const debouncedInputValue = useDebounce(inputValue, 300)
  const hasOptions = options.length > 0

  const invalidateOptions = () => {
    fetchSequenceRef.current += 1
    setOptions([])
  }

  const {
    activateOption,
    activeIndex,
    activeOptionId,
    close,
    handleKeyDown,
    isOpen,
    listboxId,
    openWithFirstOption,
    openWithoutActiveOption,
  } = useComboboxListbox({
    id: id ? `${id}-options` : undefined,
    optionCount: options.length,
    onClose: invalidateOptions,
  })

  const fetchAndSetOptions = useEffectEvent(async (input: string | null | undefined) => {
    const fetchSequence = fetchSequenceRef.current + 1
    fetchSequenceRef.current = fetchSequence
    const searchInput = input ?? ''

    if (searchInput.length < minChars) {
      if (fetchSequenceRef.current === fetchSequence) setOptions([])
      return
    }

    try {
      const fetchedOptions = await fetchOptions(searchInput)

      if (fetchSequenceRef.current !== fetchSequence) return

      const nextOptions = fetchedOptions || []
      setOptions(nextOptions)

      if (nextOptions.length > 0) openWithFirstOption()
    } catch (error) {
      if (fetchSequenceRef.current !== fetchSequence) return

      onFetchError?.(error)
      console.error('Error fetching autocomplete options:', error)
      setOptions([])
    }
  })

  useEffect(() => {
    if (!isOpen) return
    // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
    void fetchAndSetOptions(debouncedInputValue)
  }, [debouncedInputValue, isOpen])

  const handleOptionSelect = (option: TOption) => {
    close()
    onOptionSelected?.(option)
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const nextFocusTarget = event.relatedTarget
    const isMovingToOptionsList =
      nextFocusTarget instanceof HTMLElement &&
      nextFocusTarget.closest('[data-autocomplete-options-root="true"]')

    if (!isMovingToOptionsList) close()
  }

  const handleValueChange = (nextValue: string) => {
    invalidateOptions()
    onChange?.(nextValue)
    openWithoutActiveOption()
  }

  const input = (
    <Input
      {...props}
      ref={inputRef}
      id={id}
      placeholder={placeholder}
      value={inputValue}
      role="combobox"
      aria-autocomplete="list"
      aria-expanded={isOpen && hasOptions}
      aria-controls={isOpen && hasOptions ? listboxId : undefined}
      aria-activedescendant={activeOptionId}
      autoComplete={allowAutofill ? undefined : 'one-time-code'}
      onChange={(event) => handleValueChange(event.target.value)}
      onFocus={() => (hasOptions ? openWithFirstOption() : openWithoutActiveOption())}
      onBlur={handleBlur}
      onKeyDown={(event) =>
        handleKeyDown(event, (index) => {
          // The hook clamps the active index to `options.length`, so this lookup always hits.
          const option = options[index]
          if (option !== undefined) handleOptionSelect(option)
        })
      }
      className={cn('w-full', inputClassName)}
      disabled={disabled}
    />
  )

  const listbox = (
    <ComboboxListbox
      items={options}
      listboxId={listboxId}
      activeIndex={activeIndex}
      renderOption={renderOption}
      containWheel={containWheel}
      onActivateOption={activateOption}
      onSelectOption={handleOptionSelect}
    />
  )

  return (
    <div className={cn('relative flex w-full flex-col gap-y-2', className)}>
      {label ? (
        <div className="inline-flex items-center">
          <Label htmlFor={id}>{label}</Label>
        </div>
      ) : null}
      {dropdownInPortal ? (
        <Popover open={isOpen && hasOptions} onOpenChange={(open) => !open && close()}>
          <PopoverAnchor asChild>{input}</PopoverAnchor>
          <PopoverContent
            align="start"
            size="trigger"
            padding="none"
            elevation="subtle"
            onOpenAutoFocus={(event) => event.preventDefault()}
            onInteractOutside={(event) => {
              if (event.target === inputRef.current) event.preventDefault()
            }}
          >
            {listbox}
          </PopoverContent>
        </Popover>
      ) : (
        <div className="relative">
          {input}
          {isOpen && hasOptions ? (
            <div className="absolute left-0 top-full z-10 mt-2 w-full rounded-md bg-popover shadow-sm ring-1 ring-foreground/10">
              {listbox}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
